import { Card, ContentState, StatusMessage } from "@whitehaven/ui";
import type { AppointmentId } from "./scheduling-contract";
import { AdminSchedulingNavigation } from "./AdminSchedulingNavigation";
import { SchedulingMoveResizeControls } from "./SchedulingMoveResizeControls";
import {
  buildAdminSchedulingViewGroups,
  formatSchedulingTimestamp,
  type AdminSchedulingAppointment,
  type AdminSchedulingAppointmentMutationRequest,
  type AdminSchedulingUndoGrant,
  type AdminSchedulingView,
  type AdminSchedulingWorkspaceSnapshot,
} from "./scheduling-portal-model";

export interface AdminSchedulingWorkspaceFeedback {
  readonly tone: "positive" | "critical";
  readonly message: string;
  readonly refreshRequired: boolean;
  readonly undo: AdminSchedulingUndoGrant | null;
}

export interface AdminSchedulingWorkspaceViewProps {
  readonly view: AdminSchedulingView;
  readonly snapshot: AdminSchedulingWorkspaceSnapshot;
  readonly canWrite: boolean;
  readonly pendingAppointmentId?: AppointmentId | null;
  readonly optimisticAppointmentId?: AppointmentId | null;
  readonly workspaceBusy?: boolean;
  readonly feedbackByAppointmentId?: ReadonlyMap<
    AppointmentId,
    AdminSchedulingWorkspaceFeedback
  >;
  readonly onMutationRequest: (
    request: AdminSchedulingAppointmentMutationRequest,
  ) => void;
  readonly onRefresh: () => void;
  readonly onUndo: (
    appointmentId: AppointmentId,
    grant: AdminSchedulingUndoGrant,
  ) => void;
}

const viewTitles: Readonly<Record<AdminSchedulingView, string>> = {
  day: "Day schedule",
  week: "Week schedule",
  "resource-lanes": "Resource lanes",
  list: "Appointment list",
  kanban: "Appointment status board",
};

function itemLabel(
  appointment: AdminSchedulingAppointment,
  snapshot: AdminSchedulingWorkspaceSnapshot,
): string {
  return (
    snapshot.services.find((service) => service.id === appointment.serviceId)
      ?.displayName ?? "Scheduled service"
  );
}

function locationLabel(
  appointment: AdminSchedulingAppointment,
  snapshot: AdminSchedulingWorkspaceSnapshot,
): string {
  return (
    snapshot.locations.find(
      (location) => location.id === appointment.locationId,
    )?.displayName ?? "Unknown location"
  );
}

function AppointmentCard({
  appointment,
  snapshot,
  canWrite,
  pending,
  optimistic,
  workspaceBusy,
  feedback,
  onMutationRequest,
  onRefresh,
  onUndo,
}: {
  readonly appointment: AdminSchedulingAppointment;
  readonly snapshot: AdminSchedulingWorkspaceSnapshot;
  readonly canWrite: boolean;
  readonly pending: boolean;
  readonly optimistic: boolean;
  readonly workspaceBusy: boolean;
  readonly feedback: AdminSchedulingWorkspaceFeedback | null;
  readonly onMutationRequest: (
    request: AdminSchedulingAppointmentMutationRequest,
  ) => void;
  readonly onRefresh: () => void;
  readonly onUndo: (
    appointmentId: AppointmentId,
    grant: AdminSchedulingUndoGrant,
  ) => void;
}) {
  return (
    <Card
      id={`schedule-${appointment.appointmentId}`}
      className="scheduling-appointment-card"
      headingLevel={3}
      eyebrow={appointment.status.replaceAll("_", " ")}
      title={itemLabel(appointment, snapshot)}
      description={`${locationLabel(appointment, snapshot)} · ${formatSchedulingTimestamp(
        appointment.startsAt,
        appointment.timezone,
      )}`}
      footer={`Revision ${appointment.revision}${optimistic ? " · pending preview" : ""}`}
    >
      <dl data-scheduling-optimistic={optimistic || undefined}>
        <div>
          <dt>Duration</dt>
          <dd>{appointment.durationMinutes} minutes</dd>
        </div>
        <div>
          <dt>Resources</dt>
          <dd>
            {appointment.resourceIds
              .map(
                (resourceId) =>
                  snapshot.resources.find(
                    (resource) => resource.id === resourceId,
                  )?.displayLabel ?? "Unknown resource",
              )
              .join(", ") || "Unassigned"}
          </dd>
        </div>
      </dl>
      {feedback && (
        <StatusMessage
          tone={feedback.tone}
          title={feedback.message}
          live
          className="scheduling-appointment-feedback"
        >
          {feedback.refreshRequired && (
            <button type="button" onClick={onRefresh}>
              Refresh schedule
            </button>
          )}
          {feedback.undo && !feedback.refreshRequired && (
            <button
              type="button"
              onClick={() => onUndo(appointment.appointmentId, feedback.undo!)}
            >
              Undo last move
            </button>
          )}
        </StatusMessage>
      )}
      {canWrite && (
        <SchedulingMoveResizeControls
          appointment={appointment}
          resources={snapshot.resources}
          busy={pending}
          disabled={
            feedback?.refreshRequired === true || (workspaceBusy && !pending)
          }
          disabledReason={
            feedback?.refreshRequired
              ? "Refresh the schedule before editing this appointment."
              : workspaceBusy && !pending
                ? "Another scheduling change is being confirmed."
                : "Schedule editing is unavailable."
          }
          onMutationRequest={onMutationRequest}
        />
      )}
    </Card>
  );
}

export function AdminSchedulingWorkspaceView({
  view,
  snapshot,
  canWrite,
  pendingAppointmentId = null,
  optimisticAppointmentId = null,
  workspaceBusy = false,
  feedbackByAppointmentId = new Map(),
  onMutationRequest,
  onRefresh,
  onUndo,
}: AdminSchedulingWorkspaceViewProps) {
  const groups = buildAdminSchedulingViewGroups(snapshot, view);
  const count = groups.reduce(
    (total, group) => total + group.appointments.length,
    0,
  );

  return (
    <section
      className={`portal-page scheduling-page scheduling-page--${view}`}
      aria-labelledby="portal-title"
      aria-describedby="scheduling-workspace-description"
      data-scheduling-view={view}
    >
      <header className="portal-page__header">
        <div>
          <p className="portal-kicker">Scheduling workspace</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            {viewTitles[view]}
          </h1>
          <p id="scheduling-workspace-description">
            Review confirmed scheduling data for {snapshot.anchorDate}. Pointer
            drag-and-drop and duration handles are paired with native
            keyboard-operable move and resize buttons.
          </p>
        </div>
      </header>
      <AdminSchedulingNavigation current={{ kind: "workspace", value: view }} />
      {!canWrite && (
        <ContentState
          kind="warning"
          title="Schedule is read-only"
          headingLevel={2}
          live={false}
        >
          Your current permissions or the configured gateway do not allow
          schedule changes.
        </ContentState>
      )}
      <p aria-live="polite">
        {count} appointment{count === 1 ? "" : "s"} in this view.
      </p>
      <div className={`scheduling-groups scheduling-groups--${view}`}>
        {groups.map((group) => (
          <section
            className="scheduling-group"
            aria-labelledby={`scheduling-group-${group.id}`}
            key={group.id}
            data-scheduling-group={group.id}
          >
            <h2 id={`scheduling-group-${group.id}`}>
              {group.label} ({group.appointments.length})
            </h2>
            {group.appointments.length === 0 ? (
              <p>No appointments in this group.</p>
            ) : (
              <div className="scheduling-appointment-grid">
                {group.appointments.map((appointment) => (
                  <AppointmentCard
                    appointment={appointment}
                    snapshot={snapshot}
                    canWrite={canWrite}
                    pending={pendingAppointmentId === appointment.appointmentId}
                    optimistic={
                      optimisticAppointmentId === appointment.appointmentId
                    }
                    workspaceBusy={workspaceBusy}
                    feedback={
                      feedbackByAppointmentId.get(appointment.appointmentId) ??
                      null
                    }
                    onMutationRequest={onMutationRequest}
                    onRefresh={onRefresh}
                    onUndo={onUndo}
                    key={appointment.appointmentId}
                  />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </section>
  );
}
